import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

const DAYS_OF_WEEK = [
  { iso: 1, label: "Mon" },
  { iso: 2, label: "Tue" },
  { iso: 3, label: "Wed" },
  { iso: 4, label: "Thu" },
  { iso: 5, label: "Fri" },
  { iso: 6, label: "Sat" },
  { iso: 7, label: "Sun" },
];

/**
 * Plan working-calendar settings. Edits go directly through plansApi.updatePlan
 * passed in via `onSave(payload)`.
 */
export default function WorkingCalendarDialog({ open, onOpenChange, plan, onSave }) {
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !plan) return;
    setWorkingDays(plan.working_days || [1, 2, 3, 4, 5]);
    setHoursPerDay(plan.working_hours_per_day || 8);
    setHolidays((plan.holidays || []).slice().sort());
    setNewHoliday("");
  }, [open, plan]);

  const toggleDay = (iso) => {
    setWorkingDays((cur) =>
      cur.includes(iso) ? cur.filter((d) => d !== iso) : [...cur, iso].sort(),
    );
  };

  const addHoliday = () => {
    if (!newHoliday) return;
    if (holidays.includes(newHoliday)) {
      toast.error("That date is already a holiday");
      return;
    }
    setHolidays((cur) => [...cur, newHoliday].sort());
    setNewHoliday("");
  };

  const removeHoliday = (iso) => {
    setHolidays((cur) => cur.filter((d) => d !== iso));
  };

  const handleSave = async () => {
    if (workingDays.length === 0) {
      toast.error("At least one working day is required");
      return;
    }
    if (hoursPerDay < 1 || hoursPerDay > 24) {
      toast.error("Hours per day must be between 1 and 24");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        working_days: workingDays,
        working_hours_per_day: Number(hoursPerDay),
        holidays,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-lg"
        data-testid="working-calendar-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Working Calendar
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Days and hours used by cascade math, duration parsing, and over-allocation flagging.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-overline text-zinc-500">Working Days</Label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((d) => {
                const on = workingDays.includes(d.iso);
                return (
                  <button
                    key={d.iso}
                    type="button"
                    data-testid={`calendar-day-${d.iso}`}
                    onClick={() => toggleDay(d.iso)}
                    className={`min-w-[52px] px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wide transition-colors ${
                      on
                        ? "bg-amber-500 text-black border border-amber-500"
                        : "bg-zinc-900 text-zinc-500 border border-zinc-800 hover:text-zinc-300"
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Hours per Working Day</Label>
            <Input
              data-testid="calendar-hours"
              type="number"
              min={1}
              max={24}
              value={hoursPerDay}
              onChange={(e) => setHoursPerDay(Number(e.target.value) || 8)}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 font-mono w-32"
            />
            <p className="text-xs text-zinc-500">
              Default is 8 hours. Increase for crunch shoots or decrease for half-day blocks.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-overline text-zinc-500">Holidays</Label>
            <div className="flex gap-2">
              <Input
                data-testid="calendar-holiday-input"
                type="date"
                value={newHoliday}
                onChange={(e) => setNewHoliday(e.target.value)}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
              <Button
                type="button"
                data-testid="calendar-holiday-add"
                onClick={addHoliday}
                disabled={!newHoliday}
                className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm disabled:opacity-40"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
            {holidays.length > 0 ? (
              <ul className="space-y-1.5 max-h-40 overflow-auto">
                {holidays.map((iso) => (
                  <li
                    key={iso}
                    data-testid={`calendar-holiday-${iso}`}
                    className="flex items-center justify-between rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-1.5 text-sm font-mono"
                  >
                    <span className="text-zinc-200">{iso}</span>
                    <button
                      type="button"
                      data-testid={`calendar-holiday-remove-${iso}`}
                      onClick={() => removeHoliday(iso)}
                      className="p-1 text-zinc-500 hover:text-rose-400 rounded-sm hover:bg-rose-950/30"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-zinc-500 italic">
                No holidays. Add federal/local dates to keep cascade from scheduling through them.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="calendar-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
